// Main map page. Filter-driven (no more views) — server evaluates filters
// against a cached accounts.json and returns matching GeoJSON features.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import MapView from '../components/MapView';
import SyncButton from '../components/SyncButton';
import AccountPopup from '../components/AccountPopup';
import FilterBuilder from '../components/FilterBuilder';
import { api, type GeoJsonFeature, type PopupPreset, type SavedFilter } from '../lib/api';
import type { Combinator, FilterRule } from '../lib/filters';
import { WITHIN_FIELD, decodeRules, isRuleComplete } from '../lib/filters';
import { useAuth, isAdmin } from '../lib/auth';

const ACTIVE_PRESET_STORAGE_KEY = 'mapsly.activePresetId';

interface OpenPopup {
  feature: GeoJsonFeature;
  pinned: boolean;
}

function sanitizeRulesForQuery(rules: FilterRule[]): FilterRule[] {
  return rules.filter(isRuleComplete);
}

function rulesSignature(rules: FilterRule[], combinator: Combinator): string {
  return JSON.stringify({
    c: combinator,
    r: rules.map((r) => ({ f: r.field, o: r.operator, v: r.value, k: r.center ?? null })),
  });
}

/** A filter (and a pin to focus) handed over in the URL, e.g. by a CRM module: ?rules=<base64url json>&focus=<deal id>. */
function readUrlState(): { rules: FilterRule[]; combinator: Combinator; focus: string | null; maptype: string | null } | null {
  const q = new URLSearchParams(window.location.search);
  const rules = q.get('rules');
  const focus = q.get('focus');
  const maptype = q.get('maptype');
  if (!rules && !focus && !maptype) return null;
  const decoded = rules ? decodeRules(rules) : null;
  return { rules: decoded?.rules ?? [], combinator: decoded?.combinator ?? 'AND', focus, maptype };
}

export default function MapPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [urlState] = useState(() => readUrlState());
  const [openPopups, setOpenPopups] = useState<Map<string, OpenPopup>>(new Map());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(() => (urlState?.rules.length ?? 0) > 0);

  // Draft state (edited in FilterBuilder)
  const [draftRules, setDraftRules] = useState<FilterRule[]>(urlState?.rules ?? []);
  const [draftCombinator, setDraftCombinator] = useState<Combinator>(urlState?.combinator ?? 'AND');

  // Applied state (what the server last ran against)
  const [appliedRules, setAppliedRules] = useState<FilterRule[]>(urlState?.rules ?? []);
  const [appliedCombinator, setAppliedCombinator] = useState<Combinator>(urlState?.combinator ?? 'AND');
  const focusId = urlState?.focus ?? null;
  const mapTypeId = urlState?.maptype === 'satellite' || urlState?.maptype === 'hybrid' || urlState?.maptype === 'roadmap' ? urlState.maptype : undefined;

  const { data: configData } = useQuery({
    queryKey: ['config'],
    queryFn: () => api.getConfig(),
    staleTime: 5 * 60_000,
  });

  const filterUIOpen = filtersExpanded || drawerOpen;
  const { data: fieldsData } = useQuery({
    queryKey: ['fields'],
    queryFn: () => api.getFields(),
    enabled: filterUIOpen,
    staleTime: 5 * 60_000,
  });

  const { data: fieldTypesData } = useQuery({
    queryKey: ['fieldTypes'],
    queryFn: () => api.getFieldTypes(),
    enabled: filterUIOpen,
    staleTime: 5 * 60_000,
  });

  const { data: savedFiltersData } = useQuery({
    queryKey: ['savedFilters'],
    queryFn: () => api.listFilters(),
    enabled: filterUIOpen,
    staleTime: 60_000,
  });
  const savedFilters = savedFiltersData?.filters ?? [];

  // Saved-filter UI: name box for the pending save, and busy/error flags.
  const [saveName, setSaveName] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Deleting a saved filter is two-step: first click arms the row, second confirms.
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const sanitizedApplied = useMemo(() => sanitizeRulesForQuery(appliedRules), [appliedRules]);

  const { data: filterData, isFetching: filterFetching } = useQuery({
    queryKey: ['filter', rulesSignature(sanitizedApplied, appliedCombinator)],
    queryFn: () => api.applyFilter(sanitizedApplied, appliedCombinator),
    staleTime: 60_000,
  });

  const fallbackPreset: PopupPreset = {
    id: 'default',
    name: 'Default',
    popupFields: ['name', 'address', 'city', 'state', 'zipCode'],
    popupTitle: 'name',
  };
  const presets: PopupPreset[] = configData?.config.presets ?? [fallbackPreset];

  const [activePresetId, setActivePresetId] = useState<string>(
    () => localStorage.getItem(ACTIVE_PRESET_STORAGE_KEY) ?? '',
  );
  const activePreset =
    presets.find((p) => p.id === activePresetId) ?? presets[0] ?? fallbackPreset;
  useEffect(() => {
    localStorage.setItem(ACTIVE_PRESET_STORAGE_KEY, activePreset.id);
  }, [activePreset.id]);

  // The focused pin (the deal a CRM link came from) is shown even when it does not match the filter, with its popup open.
  const { data: focusData } = useQuery({
    queryKey: ['account', focusId ?? '_'],
    queryFn: () => api.getAccount(focusId!),
    enabled: !!focusId,
    staleTime: 5 * 60_000,
  });
  const focusFeature = useMemo<GeoJsonFeature | null>(() => {
    const a = focusData?.account;
    if (!a || a.lat == null || a.lng == null) return null;
    return { type: 'Feature', geometry: { type: 'Point', coordinates: [a.lng, a.lat] }, properties: { id: a.id, name: a.name, address: [a.address, a.city, a.state, a.zipCode].filter(Boolean).join(', ') } };
  }, [focusData]);

  // The applied radius rule, drawn on the map and used to frame the view (the same ring the CRM's picture shows).
  const ring = useMemo(() => {
    const r = sanitizedApplied.find((x) => x.field === WITHIN_FIELD && x.center);
    return r ? { lat: r.center!.lat, lng: r.center!.lng, radiusMi: Number(r.value) } : null;
  }, [sanitizedApplied]);

  const features: GeoJsonFeature[] = useMemo(() => {
    const base = filterData?.features ?? [];
    if (!focusFeature || base.some((f) => f.properties.id === focusFeature.properties.id)) return base;
    return [...base, focusFeature];
  }, [filterData, focusFeature]);
  const matched = filterData?.matched ?? features.length;
  const total = filterData?.total ?? 0;

  useEffect(() => {
    if (!focusFeature) return;
    setOpenPopups((prev) => (prev.has(focusFeature.properties.id) ? prev : new Map(prev).set(focusFeature.properties.id, { feature: focusFeature, pinned: true })));
  }, [focusFeature]);

  const handleMarkerClick = useCallback((feature: GeoJsonFeature) => {
    setOpenPopups((prev) => {
      const next = new Map<string, OpenPopup>();
      for (const [id, entry] of prev) {
        if (entry.pinned) next.set(id, entry);
      }
      const id = feature.properties.id;
      if (!next.has(id)) next.set(id, { feature, pinned: false });
      return next;
    });
  }, []);

  const handlePopupClose = useCallback((featureId: string) => {
    setOpenPopups((prev) => {
      if (!prev.has(featureId)) return prev;
      const next = new Map(prev);
      next.delete(featureId);
      return next;
    });
  }, []);

  const handleTogglePin = useCallback((featureId: string) => {
    setOpenPopups((prev) => {
      const entry = prev.get(featureId);
      if (!entry) return prev;
      const next = new Map(prev);
      next.set(featureId, { ...entry, pinned: !entry.pinned });
      return next;
    });
  }, []);

  const popupSize = configData?.config.popupSize ?? 'medium';
  const popups = Array.from(openPopups.values()).map(({ feature, pinned }) => ({
    feature,
    content: (
      <AccountPopup
        accountId={feature.properties.id}
        preset={activePreset}
        size={popupSize}
        pinned={pinned}
        onTogglePin={() => handleTogglePin(feature.properties.id)}
        onClose={() => handlePopupClose(feature.properties.id)}
      />
    ),
  }));

  const availableFields = fieldsData?.fields ?? [];
  const fieldLabels = fieldsData?.labels ?? {};
  const fieldTypes = fieldTypesData?.types ?? {};

  const isDirty =
    rulesSignature(sanitizeRulesForQuery(draftRules), draftCombinator) !==
    rulesSignature(sanitizedApplied, appliedCombinator);

  const activeDraftCount = draftRules.filter(isRuleComplete).length;
  const filterCountLabel = activeDraftCount > 0 ? ` (${activeDraftCount})` : '';

  function applyFilters() {
    setAppliedRules(draftRules);
    setAppliedCombinator(draftCombinator);
    setOpenPopups(new Map());
  }

  function clearFilters() {
    setDraftRules([]);
    setDraftCombinator('AND');
    setAppliedRules([]);
    setAppliedCombinator('AND');
    setOpenPopups(new Map());
  }

  // Load a saved filter into the builder and apply it immediately.
  function loadSavedFilter(f: SavedFilter) {
    setDraftRules(f.rules);
    setDraftCombinator(f.combinator);
    setAppliedRules(f.rules);
    setAppliedCombinator(f.combinator);
    setOpenPopups(new Map());
  }

  async function saveCurrentFilter() {
    const name = saveName.trim();
    const rules = sanitizeRulesForQuery(draftRules);
    if (!name || rules.length === 0) return;
    setSaveBusy(true);
    setSaveError(null);
    try {
      await api.saveFilter(name, rules, draftCombinator);
      await queryClient.invalidateQueries({ queryKey: ['savedFilters'] });
      setSaveName('');
      setSaveOpen(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaveBusy(false);
    }
  }

  async function deleteSavedFilter(id: string) {
    await api.deleteFilter(id);
    setConfirmDeleteId(null);
    await queryClient.invalidateQueries({ queryKey: ['savedFilters'] });
  }

  const filterBuilder = (
    <div className="flex flex-col gap-3">
      <FilterBuilder
        rules={draftRules}
        combinator={draftCombinator}
        availableFields={availableFields}
        fieldTypes={fieldTypes}
        fieldLabels={fieldLabels}
        onChange={setDraftRules}
        onCombinatorChange={setDraftCombinator}
        onClear={() => setDraftRules([])}
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={applyFilters}
          disabled={!isDirty || filterFetching}
          className="bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {filterFetching ? 'Applying…' : isDirty ? 'Apply filters' : 'Applied'}
        </button>
        {(appliedRules.length > 0 || draftRules.length > 0) && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-xs text-gray-500 hover:text-gray-800"
          >
            Reset
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            setSaveError(null);
            setSaveOpen((v) => !v);
          }}
          disabled={activeDraftCount === 0}
          className="text-xs text-brand-600 hover:text-brand-700 disabled:opacity-40"
        >
          Save filter
        </button>
        <span className="text-xs text-gray-400 ml-auto">
          {appliedRules.length === 0
            ? `${total.toLocaleString()} accounts`
            : `${matched.toLocaleString()} of ${total.toLocaleString()} accounts`}
        </span>
      </div>

      {saveOpen && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveCurrentFilter();
              }}
              placeholder="Filter name"
              autoFocus
              className="border border-gray-300 bg-white px-2 py-1 text-sm focus:border-brand-500 focus:outline-none"
            />
            <button
              type="button"
              onClick={saveCurrentFilter}
              disabled={saveBusy || saveName.trim() === ''}
              className="bg-brand-600 px-3 py-1 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {saveBusy ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => {
                setSaveOpen(false);
                setSaveName('');
                setSaveError(null);
              }}
              className="text-xs text-gray-500 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
          {saveError && <span className="text-xs text-red-600">{saveError}</span>}
        </div>
      )}

      {savedFilters.length > 0 && (
        <div className="flex flex-col gap-1 border-t border-gray-200 pt-2">
          <span className="text-xs font-medium text-gray-500">Saved filters</span>
          <ul className="flex flex-col gap-0.5">
            {savedFilters.map((f) => (
              <li key={f.id} className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => loadSavedFilter(f)}
                  className="flex-1 truncate text-left text-sm text-brand-700 hover:underline"
                  title={f.name}
                >
                  {f.name}
                </button>
                {confirmDeleteId === f.id ? (
                  <span className="flex items-center gap-2 whitespace-nowrap text-xs">
                    <span className="text-gray-600">Delete permanently?</span>
                    <button
                      type="button"
                      onClick={() => deleteSavedFilter(f.id)}
                      className="border border-red-200 bg-white px-2 py-0.5 text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(null)}
                      className="text-gray-500 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteId(f.id)}
                    aria-label={`Delete ${f.name}`}
                    title="Delete this saved filter"
                    className="text-xs text-gray-400 hover:text-red-600"
                  >
                    Delete
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  const pinsSummary = (() => {
    if (filterFetching && features.length === 0) return 'Loading…';
    if (features.length === 0 && appliedRules.length > 0) return 'No matches';
    if (appliedRules.length === 0) return `${features.length.toLocaleString()} pins`;
    return `${matched.toLocaleString()} of ${total.toLocaleString()} pins`;
  })();

  const presetSelect = (
    <select
      id="preset-select"
      value={activePreset.id}
      onChange={(e) => setActivePresetId(e.target.value)}
      className="border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
    >
      {presets.map((p) => (
        <option key={p.id} value={p.id}>
          {p.name}
        </option>
      ))}
    </select>
  );

  return (
    <div className="flex h-full flex-col">
      {/* Desktop toolbar */}
      <div className="hidden md:flex items-center justify-between border-b border-gray-200 bg-white px-4 py-2">
        <div className="flex items-center gap-4">
          <h1 className="font-display text-lg font-semibold text-brand-700">Mapsly</h1>
          <div className="flex items-center gap-2">
            <label
              htmlFor="preset-select"
              className="text-sm font-medium text-gray-700 whitespace-nowrap"
            >
              Display Fields:
            </label>
            {presetSelect}
          </div>
          <button
            type="button"
            onClick={() => setFiltersExpanded((v) => !v)}
            className="border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-brand-50 hover:border-brand-300"
          >
            {filtersExpanded ? 'Hide filters' : 'Filters'}
            {filterCountLabel}
          </button>
          <span className="text-xs text-gray-400">{pinsSummary}</span>
        </div>
        <div className="flex items-center gap-4">
          <SyncButton />
          {user && isAdmin(user) && (
            <Link
              to="/admin"
              className="text-sm text-brand-600 hover:text-brand-700 no-underline hover:underline"
            >
              Admin
            </Link>
          )}
        </div>
      </div>

      {filtersExpanded && (
        <div className="hidden md:block border-b border-gray-200 bg-gray-50 px-4 py-3">
          {filterBuilder}
        </div>
      )}

      {/* Mobile toolbar — title + hamburger */}
      <div className="flex md:hidden items-center gap-3 border-b border-gray-200 bg-white px-3 py-2">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open menu"
          className="p-1.5 text-gray-600 hover:text-gray-900"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <h1 className="font-display text-base font-semibold text-brand-700">Mapsly</h1>
        <span className="ml-auto text-xs text-gray-400">{pinsSummary}</span>
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40 md:hidden"
            onClick={() => setDrawerOpen(false)}
            aria-hidden
          />
          <aside className="fixed top-0 left-0 bottom-0 w-80 max-w-[85vw] bg-white z-50 md:hidden flex flex-col overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h1 className="font-display text-lg font-semibold text-brand-700">Mapsly</h1>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close menu"
                className="p-1 text-gray-500 hover:text-gray-800"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>

            <div className="flex flex-col gap-4 px-4 py-4">
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="preset-select-mobile"
                  className="text-sm font-medium text-gray-700"
                >
                  Display Fields
                </label>
                <select
                  id="preset-select-mobile"
                  value={activePreset.id}
                  onChange={(e) => setActivePresetId(e.target.value)}
                  className="border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
                >
                  {presets.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pt-2 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Filters{filterCountLabel}
                  </label>
                </div>
                {filterBuilder}
              </div>

              <div className="pt-2 border-t border-gray-200">
                <SyncButton />
              </div>

              {user && isAdmin(user) && (
                <Link
                  to="/admin"
                  className="text-sm text-brand-600 hover:text-brand-700 no-underline hover:underline"
                  onClick={() => setDrawerOpen(false)}
                >
                  Admin →
                </Link>
              )}
            </div>
          </aside>
        </>
      )}

      <div className="relative flex-1">
        <MapView
          features={features}
          onMarkerClick={handleMarkerClick}
          popups={popups}
          onPopupClose={handlePopupClose}
          autoZoom={configData?.config.autoZoom ?? 'on-features-change'}
          selectedViewId={null}
          clusterMarkers={configData?.config.clusterMarkers ?? true}
          ring={ring}
          mapTypeId={mapTypeId}
        />
      </div>
    </div>
  );
}
